/**
 * InviteUserDialog Component
 * 
 * Dialog for inviting a new user to the workspace.
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";

import { WorkspaceRole, CustomRole } from "../../../types";

export interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: () => Promise<void>;
  inviteEmail: string;
  setInviteEmail: (email: string) => void;
  inviteRole: WorkspaceRole;
  setInviteRole: (role: WorkspaceRole) => void;
  inviteError: string;
  setInviteError: (error: string) => void;
  inviting: boolean;
  customRoles: CustomRole[];
  customRoleNames: Record<string, string>;
  getRoleDisplayName: (role: WorkspaceRole) => string;
}

export default function InviteUserDialog({
  open,
  onClose,
  onInvite,
  inviteEmail,
  setInviteEmail,
  inviteRole,
  setInviteRole,
  inviteError,
  setInviteError,
  inviting,
  customRoles,
  customRoleNames,
  getRoleDisplayName,
}: InviteUserDialogProps) {
  const handleClose = (): void => {
    setInviteEmail("");
    setInviteRole("user");
    setInviteError("");
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>Invite User to Workspace</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Invite a new user to collaborate on this workspace. They will receive an invitation email with a link to join.
        </Typography>
        <Stack spacing={3}>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => {
              setInviteEmail(e.target.value);
              setInviteError("");
            }}
            error={!!inviteError}
            helperText={inviteError}
            disabled={inviting}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteRole}
              label="Role"
              onChange={(e) => setInviteRole(e.target.value as WorkspaceRole)}
              disabled={inviting}
            >
              <MenuItem value="admin">
                <Box>
                  <Typography variant="body2" fontWeight="medium">Admin</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    Can manage workspace settings and all content
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="user">
                <Box>
                  <Typography variant="body2" fontWeight="medium">User</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    Can create and edit transactions, accounts, and most content
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="viewer">
                <Box>
                  <Typography variant="body2" fontWeight="medium">Viewer</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                    Can only view content, cannot make changes
                  </Typography>
                </Box>
              </MenuItem>
              {customRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">{getRoleDisplayName(role.id)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
                      Custom role
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, display: "flex" }}>
        <Button 
          onClick={handleClose} 
          disabled={inviting}
          variant="outlined"
          sx={{ flex: 1, py: 1.5 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={onInvite} 
          variant="contained" 
          disabled={inviting || !inviteEmail.trim()}
          sx={{ flex: 1, py: 1.5 }}
        >
          {inviting ? "Sending..." : "Send Invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
